/**
 * Fin de session vue depuis les appels à l'API : reconnaît une vraie expiration parmi les 401,
 * met en attente les requêtes qu'elle bloque et les rejoue une fois le même utilisateur reconnecté.
 * Branché sur toutes les instances axios (modules/axios.js).
 */
import store from "@/store";
import Sentry from "@/modules/sentry";

// Marge pour la latence entre la mesure du serveur et la réception de la réponse
const margeEcheanceMs = 60 * 1000;
// Le polling et identify repartent d'eux-mêmes après reconnexion : inutile de les rejouer
const cheminsNonMisEnAttente = ["/tache/", "/notification", "/identify/"];
// Une 401 inattendue n'est signalée qu'une fois par adresse : le polling la répéterait sinon
const unauthorizedSignales = new Set();
/**
 * Requêtes bloquées par l'expiration, rejouées une fois le même utilisateur reconnecté.
 * En mémoire uniquement : le contenu saisi par le client ne doit jamais aller dans le stockage du navigateur,
 * les postes sont souvent partagés. Hors du store car les fonctions resolve/reject ne sont pas sérialisables.
 */
let requetesEnAttente = [];

/**
 * Indique si l'échéance de session connue est passée, à l'heure du serveur.
 * Échéance inconnue (appel /session/ en échec) : on considère la session expirée plutôt que de laisser
 * le client face à des échecs silencieux.
 * @returns {Boolean}
 */
function echeanceSessionPassee() {
  const echeance = store.state.echeanceSession;
  if (!echeance) {
    return true;
  }
  return Date.now() + echeance.decalage >= echeance.expireLe - margeEcheanceMs;
}

/**
 * Seule une vraie fin de session doit afficher la modale : certaines 401 viennent d'un bug.
 * Le code est posé par le backend (projet/exceptions.py), le texte de detail étant traduit.
 * @param {Object} data Corps de la 401.
 * @returns {Boolean}
 */
function estFinDeSession(data) {
  return data?.code === "session_expiree" || (data?.code === "non_authentifie" && echeanceSessionPassee());
}

/**
 * Remonte à Sentry une 401 reçue alors que la session est censée être valide, pour retrouver le bug.
 * @param {Object} err Erreur axios.
 */
function signalerUnauthorizedInattendue(err) {
  const url = err.config?.url;
  const cle = `${err.config?.method} ${url} ${err.response.data?.code}`;
  if (unauthorizedSignales.has(cle)) {
    return;
  }
  unauthorizedSignales.add(cle);

  class UnauthorizedInattendue extends Error {
    constructor() {
      super("Unexpected 401");
      this.name = "Unexpected 401";
    }
  }

  const infos = {
    name: "UNAUTHORIZED_INATTENDUE",
    url,
    method: err.config?.method,
    code: err.response.data?.code,
    echeanceSession: store.state.echeanceSession && { ...store.state.echeanceSession },
  };
  console.warn("401 inattendue, pas de modale de session", infos);
  Sentry.client.captureException(new UnauthorizedInattendue(), { extra: infos });
}

/**
 * sansMiseEnAttente : pour les appels de la reconnexion elle-même, qui ne doivent pas s'attendre.
 * @param {Object} axiosConfig Configuration de la requête.
 * @returns {Boolean}
 */
function peutEtreMiseEnAttente(axiosConfig) {
  const url = axiosConfig?.url || "";
  return !axiosConfig?.sansMiseEnAttente && !cheminsNonMisEnAttente.some((chemin) => url.includes(chemin));
}

/**
 * Traite une erreur d'appel : une 401 d'un utilisateur connecté signale la fin de session (ou un bug),
 * et la requête bloquée par l'expiration est mise en attente.
 * @param {Object} err Erreur axios.
 * @param {Object} instance Instance axios qui a émis la requête, pour la rejouer à l'identique.
 * @returns {Promise|null} Promesse laissée en suspens si la requête est mise en attente, sinon null.
 */
export function intercepterUnauthorized(err, instance) {
  // Les visiteurs anonymes reçoivent aussi des 401 : ils ne sont pas concernés.
  // Le polling des tâches compte aussi : c'est souvent lui qui détecte la fin de session le premier.
  if (err.response?.status !== 401 || !store.state.isLogged) {
    return null;
  }
  if (!store.state.sessionExpiree) {
    if (estFinDeSession(err.response.data)) {
      store.commit("setSessionExpiree", true);
    } else {
      signalerUnauthorizedInattendue(err);
    }
  }
  if (!store.state.sessionExpiree || !peutEtreMiseEnAttente(err.config)) {
    return null;
  }
  // L'appelant n'affiche pas d'erreur : son action repartira après reconnexion
  return new Promise((resolve, reject) => {
    requetesEnAttente.push({ instance, axiosConfig: err.config, resolve, reject });
  });
}

/**
 * Rejoue dans l'ordre les requêtes bloquées, avec l'instance qui les avait émises.
 * Une 401 signifie que le serveur n'a rien traité : le rejeu ne peut pas créer de doublon.
 * @returns {Promise}
 */
export function rejouerRequetesEnAttente() {
  const requetes = requetesEnAttente;
  requetesEnAttente = [];
  return requetes.reduce(
    (precedente, { instance, axiosConfig, resolve, reject }) => precedente.then(() => {
      // Le jeton CSRF change à la connexion : axios relira le cookie plutôt que l'ancien en-tête
      axiosConfig.headers?.delete?.("X-CSRFToken");
      return instance.request(axiosConfig).then(resolve, reject);
    }),
    Promise.resolve(),
  );
}

/**
 * Oublie les requêtes bloquées sans les résoudre ni les rejeter : la page va être rechargée,
 * et un rejet afficherait des erreurs juste avant. Rien ne doit partir vers un autre compte.
 */
export function abandonnerRequetesEnAttente() {
  requetesEnAttente = [];
}
